import { onForegroundMessage, showBrowserNotification } from '@/lib/fcmService';
import { auth, db } from '@/lib/firebase-client';
import {
    deleteNotification,
    markAllNotificationsAsRead,
    markNotificationAsRead
} from '@/lib/notificationService';
import type { Notification } from '@ezrecipebox/contracts';
import { collection, doc, limit, onSnapshot, orderBy, query, type Unsubscribe } from 'firebase/firestore';
import { useEffect, useState } from 'react';

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: Error | null;
  markAsRead: (notificationId: string) => Promise<boolean>;
  markAllAsRead: () => Promise<number>;
  deleteNotification: (notificationId: string) => Promise<boolean>;
  refresh: () => void;
}

/**
 * Hook to manage in-app notifications
 * 
 * Provides real-time notifications and unread count from Firestore,
 * along with methods to mark as read and delete notifications.
 * 
 * @returns UseNotificationsReturn
 */
export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  useEffect(() => {
    const user = auth.currentUser;
    
    if (!user) {
       
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const notificationsRef = collection(db, 'users', user.uid, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    const userRef = doc(db, 'users', user.uid);

    let notificationsUnsubscribe: Unsubscribe | undefined;
    let unreadCountUnsubscribe: Unsubscribe | undefined;

    try {
      // Subscribe to notifications
      notificationsUnsubscribe = onSnapshot(
        notificationsQuery,
        (snapshot) => {
          const notifs: Notification[] = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              type: data.type,
              title: data.title,
              body: data.body,
              isRead: data.isRead ?? false,
              recipeId: data.recipeId,
              recipeBoxId: data.recipeBoxId,
              invitationToken: data.invitationToken,
              actorUid: data.actorUid,
              actorName: data.actorName,
              createdAt: data.createdAt,
              readAt: data.readAt,
            } as Notification;
          });
          setNotifications(notifs);
          setLoading(false);
        },
        (err) => {
          console.error('[useNotifications] Error fetching notifications:', err);
          setError(err as Error);
          setLoading(false);
        }
      );

      // Subscribe to unread count
      unreadCountUnsubscribe = onSnapshot(
        userRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data();
            setUnreadCount(data?.unreadNotificationCount ?? 0);
          } else {
            setUnreadCount(0);
          }
        },
        (err) => {
          console.error('[useNotifications] Error fetching unread count:', err);
        }
      );
    } catch (err) {
      console.error('[useNotifications] Error setting up listeners:', err);
      setError(err as Error);
      setLoading(false);
    }

    // Cleanup function
    return () => {
      notificationsUnsubscribe?.();
      unreadCountUnsubscribe?.();
    };
  }, [refreshTrigger]); // Include refreshTrigger to allow manual refresh

  // Setup foreground message handler
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      return;
    }

    // Handle foreground messages (when app is open)
    const unsubscribe = onForegroundMessage((payload: unknown) => {
      console.log('Foreground message received:', payload);
      const fcmPayload = payload as { notification?: { title?: string; body?: string; icon?: string }; data?: { notificationId?: string; url?: string }; fcmOptions?: { link?: string } };

      // Show browser notification
      if (fcmPayload.notification) {
        showBrowserNotification(
          fcmPayload.notification.title || 'New Notification',
          {
            body: fcmPayload.notification.body || '',
            icon: fcmPayload.notification.icon || '/vite.svg',
            tag: fcmPayload.data?.notificationId || 'default',
            data: {
              url: fcmPayload.fcmOptions?.link || fcmPayload.data?.url || '/',
              notificationId: fcmPayload.data?.notificationId,
            },
          }
        );
      }

      // Trigger refresh to get new notification from Firestore
      setRefreshTrigger((prev) => prev + 1);
    });

    return () => {
      unsubscribe?.();
    };
  }, []);

  const markAsRead = async (notificationId: string): Promise<boolean> => {
    return await markNotificationAsRead(notificationId);
  };

  const markAllAsRead = async (): Promise<number> => {
    return await markAllNotificationsAsRead();
  };

  const deleteNotif = async (notificationId: string): Promise<boolean> => {
    return await deleteNotification(notificationId);
  };

  const refresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification: deleteNotif,
    refresh,
  };
}
